import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, Loader2, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Switch } from '@shared/components/ui/switch';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Label } from '@shared/components/ui/label';
import { Separator } from '@shared/components/ui/separator';
import { toast } from '@shared/components/ui/toast-notification';
import { newsletterService } from '@shared/services';
import { useAuth } from '@shared/features/auth/context/AuthContext';
import { usePortalConfig } from '@shared/config';

export function NewsletterSettings() {
  const { user } = useAuth();
  const { id: portalId, newsletter } = usePortalConfig();
  const queryClient = useQueryClient();

  const isLexzone = portalId === 'lexzone';
  const isLexwoo = portalId === 'lexwoo';
  const isRaadVanState = isLexzone || isLexwoo;
  const weeklyNewsletterName = isRaadVanState
    ? 'wekelijkse Raad van State nieuwsbrief'
    : 'wekelijkse Hoge Raad nieuwsbrief';
  const checkIconClass = isLexzone || isLexwoo ? 'text-primary' : 'text-green-600 dark:text-green-400';

  // Fetch subscriptions for current portal only
  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ['newsletter-subscriptions', user?.id, portalId],
    queryFn: async () => {
      if (!user?.id) return [];
      return await newsletterService.getSubscriptions(user.id, portalId);
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds - fresh enough for newsletter settings
  });

  // Find specific subscriptions
  const dailySubscription = subscriptions?.find(s => s.type === 'dagelijks');
  const weeklySubscription = subscriptions?.find(s => s.type === 'wekelijks_hoge_raad');

  // Subscribe mutation
  const subscribeMutation = useMutation({
    mutationFn: async ({ type, preferPdf }: { type: 'dagelijks' | 'wekelijks_hoge_raad'; preferPdf: boolean }) => {
      if (!user?.id || !user?.email) {
        throw new Error('User not authenticated');
      }
      return await newsletterService.subscribe(user.id, user.email, type, preferPdf, portalId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'newsletter-subscriptions'
      });
      toast.success(
        `U ontvangt nu de ${variables.type === 'dagelijks' ? 'dagelijkse nieuwsbrief' : weeklyNewsletterName}.`
      );
    },
    onError: (error: Error) => {
      toast.error(`Aanmelden mislukt: ${error.message}`);
    },
  });

  // Unsubscribe mutation (portal-specific)
  const unsubscribeMutation = useMutation({
    mutationFn: async (type: 'dagelijks' | 'wekelijks_hoge_raad') => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      return await newsletterService.unsubscribe(user.id, type, portalId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'newsletter-subscriptions'
      });
      toast.success(
        `U ontvangt geen ${variables === 'dagelijks' ? 'dagelijkse nieuwsbrief' : weeklyNewsletterName} meer.`
      );
    },
    onError: (error: Error) => {
      toast.error(`Afmelden mislukt: ${error.message}`);
    },
  });

  // Update PDF preference mutation (portal-specific)
  const updatePdfMutation = useMutation({
    mutationFn: async ({ type, preferPdf }: { type: 'dagelijks' | 'wekelijks_hoge_raad'; preferPdf: boolean }) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      return await newsletterService.updatePreferences(user.id, type, preferPdf, portalId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'newsletter-subscriptions'
      });
      toast.success('Uw PDF-bijlage voorkeur is opgeslagen.');
    },
    onError: (error: Error) => {
      toast.error(`Bijwerken mislukt: ${error.message}`);
    },
  });

  // Handlers
  const handleDailyToggle = (checked: boolean) => {
    if (checked) {
      subscribeMutation.mutate({ type: 'dagelijks', preferPdf: true });
    } else {
      unsubscribeMutation.mutate('dagelijks');
    }
  };

  const handleWeeklyToggle = (checked: boolean) => {
    if (checked) {
      subscribeMutation.mutate({ type: 'wekelijks_hoge_raad', preferPdf: true });
    } else {
      unsubscribeMutation.mutate('wekelijks_hoge_raad');
    }
  };

  const handleDailyPdfToggle = (checked: boolean) => {
    updatePdfMutation.mutate({ type: 'dagelijks', preferPdf: checked });
  };

  const handleWeeklyPdfToggle = (checked: boolean) => {
    updatePdfMutation.mutate({ type: 'wekelijks_hoge_raad', preferPdf: checked });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Nieuwsbrieven
          </CardTitle>
          <CardDescription>
            Ontvang automatisch de nieuwste jurisprudentie per email
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Nieuwsbrieven
        </CardTitle>
        <CardDescription>
          Ontvang automatisch de nieuwste jurisprudentie per email
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Daily Newsletter */}
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-1">
              <Label htmlFor="daily-newsletter" className="text-base font-semibold cursor-pointer">
                Dagelijkse nieuwsbrief
              </Label>
              <p className="text-sm text-muted-foreground">
                Ontvang elke dag om {newsletter.dailySendTime} een overzicht van alle nieuwe uitspraken van de vorige dag.
              </p>
            </div>
            <Switch
              id="daily-newsletter"
              checked={!!dailySubscription?.active}
              onCheckedChange={handleDailyToggle}
              disabled={subscribeMutation.isPending || unsubscribeMutation.isPending}
            />
          </div>

          {dailySubscription?.active && (
            <div className="ml-6 pl-4 border-l-2 border-muted space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="daily-pdf"
                  checked={dailySubscription.preferPdfAttachment}
                  onCheckedChange={(checked) => handleDailyPdfToggle(checked as boolean)}
                  disabled={updatePdfMutation.isPending}
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label
                    htmlFor="daily-pdf"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Voeg PDF bijlage toe aan email
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Download alle uitspraken als PDF voor offline lezen
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Check className={`h-3 w-3 ${checkIconClass}`} />
                <span>Actief sinds {new Date(dailySubscription.createdAt).toLocaleDateString('nl-NL')}</span>
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* Weekly Hoge Raad Newsletter */}
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-1">
              <Label htmlFor="weekly-newsletter" className="text-base font-semibold cursor-pointer">
                {isRaadVanState ? 'Wekelijkse Raad van State nieuwsbrief' : 'Wekelijkse Hoge Raad nieuwsbrief'}
              </Label>
              <p className="text-sm text-muted-foreground">
                {isRaadVanState
                  ? `Ontvang elke ${newsletter.weeklySendDay} om ${newsletter.weeklySendTime} een overzicht van Raad van State uitspraken van de afgelopen week.`
                  : `Ontvang elke ${newsletter.weeklySendDay} om ${newsletter.weeklySendTime} een overzicht van Hoge Raad uitspraken van de afgelopen week.`}
              </p>
            </div>
            <Switch
              id="weekly-newsletter"
              checked={!!weeklySubscription?.active}
              onCheckedChange={handleWeeklyToggle}
              disabled={subscribeMutation.isPending || unsubscribeMutation.isPending}
            />
          </div>

          {weeklySubscription?.active && (
            <div className="ml-6 pl-4 border-l-2 border-muted space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="weekly-pdf"
                  checked={weeklySubscription.preferPdfAttachment}
                  onCheckedChange={(checked) => handleWeeklyPdfToggle(checked as boolean)}
                  disabled={updatePdfMutation.isPending}
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label
                    htmlFor="weekly-pdf"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Voeg PDF bijlage toe aan email
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {isRaadVanState ? 'Download alle Raad van State uitspraken als PDF' : 'Download alle Hoge Raad uitspraken als PDF'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Check className={`h-3 w-3 ${checkIconClass}`} />
                <span>Actief sinds {new Date(weeklySubscription.createdAt).toLocaleDateString('nl-NL')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Info footer */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground">
            <strong>Privacy:</strong> U kunt zich op elk moment afmelden via de link onderaan elke nieuwsbrief.
            Uw emailadres wordt alleen gebruikt voor het versturen van nieuwsbrieven en wordt niet gedeeld met derden.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
